import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Eye, EyeOff, ArrowUpRight } from 'lucide-react';
import { useAuth } from '../context/Auth';
import { api, message, covers } from '../lib/api';
import { BRAND } from '../lib/brand';
import { Brand, SportLabel } from '../components/Brand';
import { Btn, Field, ErrorNotice } from '../components/shared';
import '../styles/auth-headline-texture.css';
import { safeAuthNext } from '../lib/authNext';

export default function AuthPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [signup, setSignup] = useState(query.get('mode') === 'signup');
  const [role, setRole] = useState(query.get('role') === 'parent' ? 'parent' : 'trainer');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const next = query.get('next');
  const safeNext = safeAuthNext(next);

  useEffect(() => {
    if (user) navigate(safeNext || (user.role === 'parent' ? '/parent' : '/dashboard'), { replace: true });
  }, [user, navigate, safeNext]);

  const submit = async e => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      const r = await api.post('/auth/' + (signup ? 'register' : 'login'), signup ? { name, email, password, role } : { email, password });
      setUser(r.data);
      navigate(safeNext || (r.data.role === 'parent' ? '/parent' : '/dashboard'));
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  };

  const google = () => {
    sessionStorage.setItem('auth_role', role);
    if (safeNext) sessionStorage.setItem('auth_next', safeNext);
    else sessionStorage.removeItem('auth_next');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="auth-page" style={{ '--auth-image': `url(${covers[0]})` }}>
      <header className="auth-top">
        <Brand />
        <span className="auth-top-note" style={{ color: '#fff' }}>
          THIS IS WHERE THE WORK STARTS!
        </span>
      </header>
      <div className="auth-layout">
        <div className="auth-statement">
          <p className="eyebrow">Your Journey. Your Growth. Your Passport</p>
          <h1 className="auth-stone-headline" data-testid="auth-athlete-headline">
            Sports Development Starts With a Connection
          </h1>
          <p>Every Athlete Has a Next Level. Let’s Connect Yours.</p>
          <div className="auth-module"><SportLabel id="auth-sport-label" /><span data-testid="auth-module-caption">The first chapter.</span></div>
          <span className="auth-coordinate" data-testid="auth-brand-coordinate">COMMIT TO THE CRAFT</span>
        </div>
        <section className="auth-form-section">
          <div className="auth-form-heading">
            <span className="small-red-line" /><p className="eyebrow">{signup ? 'JOIN THE WORKSPACE' : 'WELCOME BACK'}</p>
            <h2 data-testid="auth-title">{signup ? 'Your next chapter.' : 'Back to the work.'}</h2>
            <p className="auth-identity-title" data-testid="auth-brand-description">{signup ? `Create your ${BRAND.name} account.` : `Sign in to ${BRAND.name}.`}</p>
          </div>
          <div className="segmented" aria-label="Account type">
            <button data-testid="role-trainer" type="button" className={role === 'trainer' ? 'selected' : ''} onClick={() => setRole('trainer')}>Trainer</button>
            <button data-testid="role-parent" type="button" className={role === 'parent' ? 'selected' : ''} onClick={() => setRole('parent')}>Parent / guardian</button>
          </div>
          <Btn data-testid="google-login-button" className="google-btn" secondary onClick={google}><span className="google-g">G</span>Continue with Google</Btn>
          <div className="divider-label"><span />or with email<span /></div>
          <form onSubmit={submit} className="form-stack">
            {signup && <Field id="auth-name" label="Full name" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} autoComplete="name" required minLength={2} />}
            <Field id="auth-email" label="Email address" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" required />
            <div className="password-field">
              <Field id="auth-password" label="Password" type={visible ? 'text' : 'password'} placeholder={signup ? 'At least 8 characters' : 'Enter your password'} value={password} onChange={e => setPassword(e.target.value)} autoComplete={signup ? 'new-password' : 'current-password'} minLength={signup ? 8 : undefined} maxLength={72} required />
              <button type="button" data-testid="password-visibility-button" onClick={() => setVisible(!visible)} aria-label={visible ? 'Hide password' : 'Show password'}>{visible ? <EyeOff size={17} /> : <Eye size={17} />}</button>
            </div>
            <ErrorNotice text={error} />
            <Btn data-testid="auth-submit-button" disabled={busy} type="submit">{busy ? 'One moment…' : signup ? 'Create account' : 'Sign in'}<ArrowRight size={17} /></Btn>
          </form>
          <p className="auth-switch"><span data-testid="auth-account-prompt">{signup ? 'Already have an account?' : `New to ${BRAND.name}?`}</span> <button data-testid="auth-mode-toggle" onClick={() => { setSignup(!signup); setError(''); }}>{signup ? 'Sign in' : 'Create an account'}<ArrowUpRight size={13} /></button></p>
          <div className="auth-privacy"><ShieldCheck size={15} /><span>Private by design. Built around your athlete.</span></div>
        </section>
      </div>
      <footer className="auth-footer">
        <span data-testid="auth-brand-footer" style={{ color: 'var(--red)' }}>
          {BRAND.name.toUpperCase()} © {new Date().getFullYear()}
        </span>
        <span style={{ color: 'var(--red)' }}>THE NEXT LEVEL IS EARNED.</span>
      </footer>
    </main>
  );
}

export const AuthCallback = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const session_id = new URLSearchParams(location.hash.slice(1)).get('session_id');
    api.post('/auth/session', { session_id, role: sessionStorage.getItem('auth_role') || 'trainer' }).then(r => {
      setUser(r.data);
      const next = sessionStorage.getItem('auth_next');
      sessionStorage.removeItem('auth_next');
      navigate(safeAuthNext(next) || (r.data.role === 'parent' ? '/parent' : '/dashboard'), { replace: true });
    }).catch(e => setError(message(e)));
  }, [location.hash, navigate, setUser]);
  return error ? <div className="callback-error"><ErrorNotice text={error} /><Link data-testid="auth-retry-link" to="/login">Return to sign in</Link></div> : null;
};